import { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "sw-update-dismissed";
const BROADCAST_SEEN_KEY = "pwa-broadcast-seen";
const POLL_INTERVAL = 30000; // 30 seconds

export default function UpdateBanner() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [broadcastUpdate, setBroadcastUpdate] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const pollRef = useRef(null);

  // Listen for native SW update
  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) {
      setDismissed(true);
    }

    const handler = (e) => {
      setWaitingWorker(e.detail);
      setDismissed(false);
      sessionStorage.removeItem(DISMISS_KEY);
    };

    window.addEventListener("sw-update-waiting", handler);
    return () => window.removeEventListener("sw-update-waiting", handler);
  }, []);

  // Poll for admin-triggered broadcast
  useEffect(() => {
    const checkBroadcast = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/pwa/check-update`);
        if (!r.ok) return;
        const data = await r.json();
        if (data.update) {
          const seen = sessionStorage.getItem(BROADCAST_SEEN_KEY);
          const broadcastId = data.broadcasted_at || "unknown";
          if (seen !== broadcastId) {
            setBroadcastUpdate(true);
            setDismissed(false);
            sessionStorage.removeItem(DISMISS_KEY);
            sessionStorage.setItem(BROADCAST_SEEN_KEY, broadcastId);
          }
        }
      } catch {
        // Silent fail — polling is best-effort
      }
    };

    checkBroadcast();
    pollRef.current = setInterval(checkBroadcast, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleRefresh = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      // controllerchange listener in index.html will reload
    } else {
      // Admin broadcast but no waiting SW — just hard reload
      window.location.reload();
    }
  }, [waitingWorker]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }, []);

  const visible = !dismissed && (waitingWorker || broadcastUpdate);
  if (!visible) return null;

  return (
    <div
      data-testid="update-banner"
      style={{
        position: "fixed",
        bottom: "16px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9997,
        width: "calc(100% - 2rem)",
        maxWidth: "440px",
        background: "#0F172A",
        border: "1px solid rgba(20, 184, 166, 0.4)",
        borderRadius: "14px",
        padding: "0.875rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
        animation: "updateSlideUp 0.3s ease-out",
      }}
    >
      <div
        style={{
          width: "38px",
          height: "38px",
          borderRadius: "10px",
          background: "linear-gradient(135deg, #14B8A6, #0D9488)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <RefreshCw size={18} color="#F8FAFC" />
      </div>
      <p
        style={{
          flex: 1,
          margin: 0,
          fontSize: "0.84rem",
          color: "#CBD5E1",
          lineHeight: 1.35,
        }}
      >
        A new version is available.
      </p>
      <button
        data-testid="update-refresh-btn"
        onClick={handleRefresh}
        style={{
          padding: "0.4rem 0.9rem",
          background: "#14B8A6",
          color: "#0F172A",
          border: "none",
          borderRadius: "50px",
          fontSize: "0.78rem",
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "background 0.2s",
        }}
        onMouseOver={(e) => (e.target.style.background = "#0D9488")}
        onMouseOut={(e) => (e.target.style.background = "#14B8A6")}
      >
        Refresh
      </button>
      <button
        data-testid="update-dismiss-btn"
        onClick={handleDismiss}
        aria-label="Dismiss update notification"
        style={{
          background: "none",
          border: "none",
          color: "#475569",
          cursor: "pointer",
          padding: "4px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <X size={16} />
      </button>
      <style>{`
        @keyframes updateSlideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}
